import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Breadcrumbs,
  Button, CircularProgress, FormControl, Grid2, Snackbar, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import AppShell from './components/AppShell';
import QuotaSection from './logic/QuotasSection';
import api from './services/api';

const initialAgeGenderRows = [
  { label: '18–24 — Male', pct: 5.29, slotValues: {} },
  { label: '18–24 — Female', pct: 5.17, slotValues: {} },
  { label: '25–34 — Male', pct: 8.3, slotValues: {} },
  { label: '25–34 — Female', pct: 8.73, slotValues: {} },
  { label: '35–44 — Male', pct: 7.97, slotValues: {} },
  { label: '35–44 — Female', pct: 8.37, slotValues: {} },
  { label: '45–54 — Male', pct: 8.23, slotValues: {} },
  { label: '45–54 — Female', pct: 8.51, slotValues: {} },
  { label: '55–64 — Male', pct: 7.84, slotValues: {} },
  { label: '55–64 — Female', pct: 8.14, slotValues: {} },
  { label: '65–74 — Male', pct: 6.06, slotValues: {} },
  { label: '65–74 — Female', pct: 6.54, slotValues: {} },
  { label: '75+ — Male', pct: 4.67, slotValues: {} },
  { label: '75+ — Female', pct: 6.18, slotValues: {} },
];

const initialEdRows = [
  { label: 'Degree', pct: 29.89, slotValues: {} },
  { label: 'No Degree', pct: 70.11, slotValues: {} },
];

const initialRegionRows = [
  { label: 'East Midlands', pct: 7.30, slotValues: {} },
  { label: 'East of England', pct: 9.44, slotValues: {} },
  { label: 'London', pct: 13.01, slotValues: {} },
  { label: 'North East', pct: 3.99, slotValues: {} },
  { label: 'North West', pct: 11.02, slotValues: {} },
  { label: 'Northern Ireland', pct: 2.76, slotValues: {} },
  { label: 'Scotland', pct: 8.38, slotValues: {} },
  { label: 'South East', pct: 13.83, slotValues: {} },
  { label: 'South West', pct: 8.67, slotValues: {} },
  { label: 'Wales', pct: 4.68, slotValues: {} },
  { label: 'West Midlands', pct: 8.77, slotValues: {} },
  { label: 'Yorkshire and The Humber', pct: 8.15, slotValues: {} },
];

const UKNatRepQuotas = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [overallN, setOverallN] = useState(1500);
  const [ageGenderState, setAgeGenderState] = useState({});
  const [educationState, setEducationState] = useState({});
  const [regionState, setRegionState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState(null); // { severity, message }

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => {
      setSurvey(res.data);
      api.get('/alchemer/questions/', { params: { survey_id: res.data.alchemer_survey_id } }).then((qRes) => {
        setQuestions(qRes.data.data.map((q) => ({ id: q.id, title: q.title, options: q.options || [] })));
      });
    });
  }, [surveyId]);

  const handleSubmit = async () => {
    const sections = [
      { name: 'Age & Gender', ...ageGenderState, namePrefix: 'Age/Gender', description: 'Age/Gender' },
      { name: 'Education', ...educationState, namePrefix: 'Education', description: 'Education' },
      { name: 'Region', ...regionState, namePrefix: 'Region', description: 'Region' },
    ].filter((s) => s.quotaRows);

    if (!sections.length) {
      setSnackbar({ severity: 'warning', message: 'No quota sections configured yet.' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post(`/surveys/${surveyId}/create-nat-rep-quotas/`, { overallN, sections });
      const { created, errors } = res.data;
      if (errors.length) {
        setSnackbar({ severity: 'warning', message: `Created ${created.length} quota(s). ${errors.length} error(s): ${errors[0]}` });
      } else {
        setSnackbar({ severity: 'success', message: `Successfully created ${created.length} quota(s) in Alchemer.` });
      }
    } catch (err) {
      setSnackbar({ severity: 'error', message: `Failed to submit quotas: ${err.response?.data?.error || err.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  if (!survey) return null;

  return (
    <AppShell maxWidth="md">
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Link to={`/survey/${surveyId}/add-quotas`} style={{ textDecoration: 'none', color: 'inherit' }}>Alchemer quota setup</Link>
        <Typography>UK NatRep Quotas</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
        <PeopleRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          UK NatRep Quotas
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 3,
          mb: 3,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">Total sample size (N)</Typography>
        <FormControl size="small">
          <TextField
            type="number"
            size="small"
            value={overallN}
            onChange={(e) => setOverallN(Number(e.target.value) || 0)}
            sx={{ width: 140 }}
          />
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Accordion defaultExpanded sx={{ border: '1px solid #e0e0e0', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Age & Gender</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <QuotaSection title="Age & Gender quotas" initialRows={initialAgeGenderRows} value={ageGenderState} onChange={setAgeGenderState} questions={questions} overallN={overallN} />
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ border: '1px solid #e0e0e0', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Education</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <QuotaSection title="Education quotas" initialRows={initialEdRows} value={educationState} onChange={setEducationState} questions={questions} overallN={overallN} />
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ border: '1px solid #e0e0e0', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Region</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <QuotaSection title="Region quotas" initialRows={initialRegionRows} value={regionState} onChange={setRegionState} questions={questions} overallN={overallN} />
          </AccordionDetails>
        </Accordion>
      </Box>

      <Grid2 container justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {submitting ? 'Submitting…' : 'Submit quotas'}
        </Button>
      </Grid2>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </AppShell>
  );
};

export default UKNatRepQuotas;
